"use client";

import * as React from "react";
import { useTranslations } from "next-intl";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UploadIcon } from "@/components/icons";
import { useUploadFileMutation } from "@/api/media/mediaApi";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new photo source (uploaded file URL or pasted link). */
  onPhotoSelected: (src: string) => void;
};

const AvatarUploadDialog: React.FC<Props> = ({ open, onOpenChange, onPhotoSelected }) => {
  const t = useTranslations();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [avatarUrl, setAvatarUrl] = React.useState("");
  const [avatarAlt, setAvatarAlt] = React.useState("");
  const [uploadFile] = useUploadFileMutation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{t("ui-add-image")}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="file" className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="file">{t("ui-upload-file")}</TabsTrigger>
            <TabsTrigger value="url">{t("ui-link")}</TabsTrigger>
          </TabsList>

          <TabsContent value="file" className="pt-4 space-y-4">
            <div
              className="flex cursor-pointer items-center justify-center rounded-lg border border-dashed border-muted-foreground/40 bg-muted/40 px-6 py-8 text-center text-sm text-muted-foreground gap-4"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-lg border bg-background">
                <UploadIcon size={32} className="text-muted-foreground" />
              </div>
              <div className="flex flex-col items-start justify-start">
                <p className="font-semibold">
                  {t("ui-upload-bg")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("ui-drag-file-here")}
                </p>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                try {
                  const result = await uploadFile({ file }).unwrap();
                  if (result.src) {
                    onPhotoSelected(result.src);
                  }
                  onOpenChange(false);
                } catch (error) {
                  console.error("Failed to upload image:", error);
                }
              }}
            />

            <div className="space-y-1">
              <label className="text-xs font-medium">{t("label-alt-text")}</label>
              <Input
                placeholder={t("placeholder-alt-text")}
                value={avatarAlt}
                onChange={(e) => setAvatarAlt(e.target.value)}
              />
            </div>
          </TabsContent>

          <TabsContent value="url" className="pt-4 space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-medium">
                {t("label-image-link")}
              </label>
              <Input
                placeholder="https://"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium">{t("label-alt-text")}</label>
              <Input
                placeholder={t("placeholder-alt-text")}
                value={avatarAlt}
                onChange={(e) => setAvatarAlt(e.target.value)}
              />
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-6 flex gap-3 w-full">
          <Button
            type="button"
            className="flex-1"
            onClick={() => {
              if (avatarUrl.trim()) {
                onPhotoSelected(avatarUrl.trim());
              }
              onOpenChange(false);
            }}
          >
            {t("btn-add")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            {t("btn-cancel")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AvatarUploadDialog;
